<script>
import { Ckeditor } from '@ckeditor/ckeditor5-vue'
import {
  Alignment,
  AutoImage,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  Underline,
  Undo,
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'
import { uploadAttachment } from '@/api/attachment.js'

class S3UploadAdapter {
  constructor(loader, uploadPath) {
    this.loader = loader
    this.uploadPath = uploadPath
  }

  async upload() {
    const file = await this.loader.file
    const res = await uploadAttachment(this.uploadPath, file)
    if (res.data?.code !== 0 || !res.data?.url) {
      throw new Error(res.data?.message || '이미지 업로드에 실패했습니다.')
    }
    return { default: res.data.url }
  }

  abort() {}
}

function createS3UploadPlugin(uploadPath) {
  return function S3UploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = loader => new S3UploadAdapter(loader, uploadPath)
  }
}

export default {
  name: 'RichTextEditor',
  components: { Ckeditor },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    uploadPath: {
      type: String,
      default: 'goldnara/editor/',
    },
    minHeight: {
      type: Number,
      default: 360,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editor: ClassicEditor,
    }
  },
  computed: {
    editorConfig() {
      return {
        licenseKey: 'GPL',
        extraPlugins: [createS3UploadPlugin(this.uploadPath)],
        plugins: [
          Alignment,
          AutoImage,
          BlockQuote,
          Bold,
          Essentials,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          Heading,
          Image,
          ImageCaption,
          ImageInsert,
          ImageResize,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
          Indent,
          Italic,
          Link,
          List,
          Paragraph,
          PasteFromOffice,
          Table,
          TableToolbar,
          Underline,
          Undo,
        ],
        toolbar: {
          items: [
            'heading',
            '|',
            'fontFamily',
            'fontSize',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'alignment',
            'bulletedList',
            'numberedList',
            'outdent',
            'indent',
            '|',
            'link',
            'insertImage',
            'insertTable',
            'blockQuote',
            '|',
            'undo',
            'redo',
          ],
          shouldNotGroupWhenFull: false,
        },
        fontFamily: {
          supportAllValues: true,
          options: [
            'default',
            'Arial, Helvetica, sans-serif',
            'Verdana, Geneva, sans-serif',
            'Times New Roman, Times, serif',
            'Courier New, Courier, monospace',
            'Noto Sans KR, sans-serif',
            'Malgun Gothic, sans-serif',
          ],
        },
        fontSize: {
          supportAllValues: true,
          options: [10, 12, 14, 'default', 18, 20, 24, 28, 32, 36],
        },
        image: {
          toolbar: [
            'imageTextAlternative',
            'toggleImageCaption',
            '|',
            'imageStyle:inline',
            'imageStyle:block',
            'imageStyle:side',
            '|',
            'resizeImage',
          ],
          resizeOptions: [
            { name: 'resizeImage:original', value: null, label: '원본' },
            { name: 'resizeImage:25', value: '25', label: '25%' },
            { name: 'resizeImage:50', value: '50', label: '50%' },
            { name: 'resizeImage:75', value: '75', label: '75%' },
          ],
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
        },
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
        },
      }
    },
  },
  methods: {
    onInput(value) {
      this.$emit('update:modelValue', value)
    },
  },
}
</script>

<template>
  <div class="rich-text-editor" :style="{ '--editor-min-height': `${minHeight}px` }">
    <Ckeditor
      :editor="editor"
      :model-value="modelValue"
      :config="editorConfig"
      @update:model-value="onInput"
    />
  </div>
</template>

<style scoped>
.rich-text-editor {
  --ck-color-base-background: #111827;
  --ck-color-base-foreground: #1f2937;
  --ck-color-base-border: rgba(100, 116, 139, 0.52);
  --ck-color-base-text: #e5e7eb;
  --ck-color-button-default-background: #1f2937;
  --ck-color-button-default-hover-background: #334155;
  --ck-color-button-default-active-background: #475569;
  --ck-color-button-on-background: rgba(205, 178, 122, 0.18);
  --ck-color-button-on-hover-background: rgba(205, 178, 122, 0.28);
  --ck-color-toolbar-background: #111827;
  --ck-color-dropdown-panel-background: #111827;
  --ck-color-dropdown-panel-border: rgba(100, 116, 139, 0.52);
  --ck-color-list-background: #111827;
  --ck-color-list-button-hover-background: #334155;
  --ck-color-list-button-on-background: rgba(205, 178, 122, 0.18);
  --ck-color-input-background: #0f172a;
  --ck-color-input-text: #e5e7eb;
  --ck-color-panel-background: #111827;
  --ck-color-panel-border: rgba(100, 116, 139, 0.52);
  --ck-color-focus-border: #cdb27a;
  --ck-color-text: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 8px;
  overflow: hidden;
  background: #111827;
}

.rich-text-editor :deep(.ck.ck-toolbar) {
  border: 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.35);
}

.rich-text-editor :deep(.ck.ck-editor__editable_inline) {
  min-height: var(--editor-min-height);
  padding: 18px 20px;
  background: #0f172a;
  color: #e5e7eb;
}

.rich-text-editor :deep(.ck.ck-editor__editable_inline.ck-focused) {
  border-color: #cdb27a;
  box-shadow: 0 0 0 2px rgba(205, 178, 122, 0.16);
}

.rich-text-editor :deep(.ck.ck-button),
.rich-text-editor :deep(.ck.ck-button .ck-button__label),
.rich-text-editor :deep(.ck.ck-dropdown__button),
.rich-text-editor :deep(.ck.ck-dropdown__button .ck-button__label) {
  color: #e5e7eb;
}

.rich-text-editor :deep(.ck.ck-icon) {
  color: #e5e7eb;
}

.rich-text-editor :deep(.ck.ck-list__item .ck-button) {
  color: #e5e7eb;
}

.rich-text-editor :deep(.ck-content p),
.rich-text-editor :deep(.ck-content li),
.rich-text-editor :deep(.ck-content blockquote) {
  color: #e5e7eb;
}

.rich-text-editor :deep(.ck-content img) {
  max-width: 100%;
}
</style>
